import { useRealtimeToasts } from "@tcg/realtime/query";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { usePermissions } from "@/lib/auth/usePermissions";
import { useToast } from "@/lib/toast";

import { playDing, useLiveAlertPrefs } from "./alertPrefs";
import { ALERT_TOPICS, alertRules } from "./alertRules";
import { allowedTopics } from "./topics";

/** Mounted once by the realtime provider: toasts + the quiet sound (settings per person). */
export function LiveAlerts({ staffId, play = playDing }: { staffId: string; play?: () => void }) {
  const { t } = useTranslation();
  const toasts = useToast();
  const { can } = usePermissions();
  const prefs = useLiveAlertPrefs(staffId);
  const topics = useMemo(() => allowedTopics(ALERT_TOPICS, can), [can]);
  const rules = useMemo(() => alertRules(t), [t]);
  const soundRef = useRef(prefs.sound);
  useEffect(() => {
    soundRef.current = prefs.sound;
  });
  useRealtimeToasts(
    topics,
    rules,
    (alert, message) => {
      toasts.show(alert.kind, alert.text, {
        dedupeKey: `live:${message.entity ?? message.topic}:${message.id}:${message.action}`,
        ...(alert.kind === "error" ? { ttl: 15_000 } : {}),
      });
      if (soundRef.current) {
        play();
      }
    },
    { enabled: prefs.toasts && topics.length > 0 },
  );
  return null;
}
